import React, { useEffect, useMemo, useState } from "react"
import { Formik, Form, Field } from "lib/formik"
import { useLocation, useNavigate } from "react-router"
import { addRoleSchemas } from "components/Common/validation"
import {
  permissionsList,
  superAdminPermissionsList,
} from "components/Common/constants"
import { useDispatch, useSelector } from "react-redux"
import {
  addNewRole,
  getRoleDetails,
  updateRoleDetails,
} from "store/actions/rolesAction"
import { userDetails } from "store/actions/authAction"
import Spinners from "components/Shared/Spinners"
import Switch from "components/Shared/Switch"
import isPermissionEnabled from "common/isPermissionEnabled"
import { showAlert } from "store/reducers/alertReducer"
import Breadcrumbs from "components/Common/Breadcrumb"
import { Card, CardBody, CardTitle, Col, Row, Button } from "reactstrap"
import RolePermissionsSelector from "components/Roles/RolePermissionsSelector"
import { Link } from "react-router-dom"
import BackButton from "components/Shared/BackButton"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"
import { ROUTES } from "constants/routes"
import { navigateToAccessibleRoute } from "utils/navigateUnauthorized"
import { get } from "helpers/api_helper"
import { TENANT_INTEGRATIONS_PERMISSION_CATEGORY_ID } from "constants/planPermissionMap"
import {
  filterPermissionCatalogByGrants,
} from "utils/planPermissionGrants"
import {
  trimRoleFormValues,
  createTrimOnBlur,
} from "utils/formUtils"

/** Integrations permission group (category id 48). */
const TENANT_INTEGRATIONS_PERMISSION_GROUP_ID =
  TENANT_INTEGRATIONS_PERMISSION_CATEGORY_ID

/** Plan integrationsTab uses `{ category, integrations }`; catalog-tree uses flat category nodes. */
const normalizeIntegrationsPlanTree = (tree = []) =>
  (Array.isArray(tree) ? tree : []).map(entry => {
    if (entry?.category && Array.isArray(entry.integrations)) {
      return { ...entry.category, integrations: entry.integrations }
    }
    return entry
  })

const initialValues = {
  name: "",
  nameAr: "",
  description: "",
  descriptionAr: "",
}

const AddRoles = ({ mode, scope = "tenant", basePath = "/roles" }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = `${mode === "add" ? t("Add") : t("Edit")} ${t(
    "Role "
  )}`

  const navigate = useNavigate()
  const location = useLocation()
  const { userRole, accountType, accountScope } = useSelector(state => state.Login)

  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const dispatch = useDispatch()
  const { loading, error, isLoading, message, actionType, roleDetails } =
    useSelector(state => state.Roles)
  const [integrationsCatalogTree, setIntegrationsCatalogTree] = useState([])
  /**
   * Effective per-integration permissions for the currently logged-in user.
   * Shape: Array<{ _id, code, name, nameAr, permissions: { tenant_integrations_1: bool, ... } }>
   * Comes from data.integrations on login/userDetails (same visibility rules as catalog-tree).
   */
  const userIntegrations = useSelector(
    state => state.Login?.userIntegrations || []
  )

  const [isCreateRolePermission, setIsCreateRolePermission] = useState(null)
  const [isEditRolePermission, setIsEditRolePermission] = useState(null)
  const [permissionLists, setPermissionLists] = useState([])
  const [openIntegrationIds, setOpenIntegrationIds] = useState([])
  /**
   * Per-integration permission map sent in the role save payload.
   * Shape: { [integrationId: string]: { tenant_integrations_1: bool, tenant_integrations_2: bool,
   *   tenant_integrations_3: bool, tenant_integrations_4: bool, tenant_integrations_5: bool } }
   * Keys match the permission ids in category 48 (constants.js) and the server's effective
   * permission object returned in data.integrations on login.
   */
  const [integrationPermissions, setIntegrationPermissions] = useState({})
  const [formValues, setFormValues] = useState(initialValues)
  const [selectAll, setSelectAll] = useState(false)
  const [roleStatus, setRoleStatus] = useState(null)

  const [selectedPermissions, setSelectedPermissions] = useState([])
  const [showSubPermissions, setShowSubPermissions] = useState(false)
  const [tenantPermissionCatalog, setTenantPermissionCatalog] = useState([])
  const [roleBuilderLoading, setRoleBuilderLoading] = useState(scope === "tenant")
  const [builderShowIntegrations, setBuilderShowIntegrations] = useState(false)
  const [integrationUiDefs, setIntegrationUiDefs] = useState([])
  const isSuperAdminSession =
    accountScope === "super_admin" || accountType === "superadmin"
  const permissionMeta =
    scope === "superadmin"
      ? {
          categoryId: 102,
          view: "sa_roles_1",
          add: "sa_roles_2",
          edit: "sa_roles_3",
        }
      : { categoryId: 3, view: "roles_1", add: "roles_2", edit: "roles_3" }
  const canViewRoles = isPermissionEnabled(
    userRole,
    permissionMeta.categoryId,
    permissionMeta.view,
    accountType,
    accountScope
  )
  const canCreateRoles = isPermissionEnabled(
    userRole,
    permissionMeta.categoryId,
    permissionMeta.add,
    accountType,
    accountScope
  )
  const canEditRoles = isPermissionEnabled(
    userRole,
    permissionMeta.categoryId,
    permissionMeta.edit,
    accountType,
    accountScope
  )
  const rolePermissionCatalog = useMemo(
    () => (scope === "superadmin" ? superAdminPermissionsList : permissionsList),
    [scope]
  )
  const filteredRolePermissionCatalog = useMemo(
    () =>
      scope === "tenant" ? tenantPermissionCatalog : rolePermissionCatalog,
    [scope, tenantPermissionCatalog, rolePermissionCatalog]
  )

  const displayPermissionGroups = useMemo(
    () =>
      selectedPermissions.filter(
        permissionGroup =>
          !(
            scope === "tenant" &&
            builderShowIntegrations &&
            Number(permissionGroup.id) === TENANT_INTEGRATIONS_PERMISSION_GROUP_ID
          )
      ),
    [selectedPermissions, scope, builderShowIntegrations]
  )

  const showIntegrationsPanel =
    scope === "tenant" && builderShowIntegrations

  const intPermDefs = useMemo(() => {
    if (scope === "tenant" && integrationUiDefs.length > 0) {
      return integrationUiDefs
    }
    const group = permissionsList.find(
      p => Number(p.id) === TENANT_INTEGRATIONS_PERMISSION_GROUP_ID
    )
    return group?.permissions || []
  }, [scope, integrationUiDefs])

  useEffect(() => {
    if (scope === "tenant" && isSuperAdminSession) {
      navigate(ROUTES.SA_ROLES)
      return
    }
    if (scope === "superadmin" && !isSuperAdminSession) {
      navigateToAccessibleRoute(navigate)
      return
    }
    if (!canViewRoles || (mode === "add" && !canCreateRoles) || (mode === "edit" && !canEditRoles)) {
      navigateToAccessibleRoute(navigate)
    }
  }, [scope, isSuperAdminSession, canViewRoles, canCreateRoles, canEditRoles, mode, navigate])

  useEffect(() => {
    if (scope !== "tenant") {
      setRoleBuilderLoading(false)
      return
    }
    let cancelled = false
    const loadRoleBuilderContext = async () => {
      setRoleBuilderLoading(true)
      try {
        const response = await get("admin/roles/role-builder-context")
        const payload = response?.data ?? response
        let categories = Array.isArray(payload?.permissionCategories)
          ? payload.permissionCategories
          : []

        if (Array.isArray(payload?.planPermissionGrants) && payload.planPermissionGrants.length) {
          categories = filterPermissionCatalogByGrants(
            categories,
            payload.planPermissionGrants
          )
        }

        const uiDefs = (payload?.integrationsPermissionUi || []).map(row => ({
          id: row.permissionId,
          label: row.displayLabel,
          labelAr: row.displayLabelAr,
        }))
        if (!cancelled) {
          setTenantPermissionCatalog(categories)
          setBuilderShowIntegrations(!!payload?.showIntegrationsTab)
          setIntegrationUiDefs(uiDefs)
          setIntegrationsCatalogTree(
            normalizeIntegrationsPlanTree(payload?.integrationsPlanTree)
          )
        }
      } catch (_error) {
        if (!cancelled) {
          setTenantPermissionCatalog([])
          setBuilderShowIntegrations(false)
          setIntegrationUiDefs([])
          setIntegrationsCatalogTree([])
        }
      } finally {
        if (!cancelled) {
          setRoleBuilderLoading(false)
        }
      }
    }
    loadRoleBuilderContext()
    return () => {
      cancelled = true
    }
  }, [scope])

  const canMutateRoles = mode === "add" ? canCreateRoles : canEditRoles

  useEffect(() => {
    if (!Array.isArray(integrationsCatalogTree) || integrationsCatalogTree.length === 0) {
      return
    }
    const flatList = integrationsCatalogTree.flatMap(cat =>
      Array.isArray(cat.integrations) ? cat.integrations : []
    )
    setIntegrationPermissions(prev => {
      const next = { ...prev }
      flatList.forEach(int => {
        const intId = String(int._id || int.id || "")
        if (intId && !next[intId]) {
          const perms = {}
          intPermDefs.forEach(p => {
            perms[p.id] = false
          })
          next[intId] = perms
        }
      })
      return next
    })
  }, [integrationsCatalogTree, intPermDefs])

  /**
   * Seed from userIntegrations (login payload) when catalog-tree hasn't loaded yet.
   * This ensures that on fast render the accordion already has entries.
   * The user's effective permission values from the server pre-fill checked state.
   */
  useEffect(() => {
    if (
      !Array.isArray(userIntegrations) ||
      userIntegrations.length === 0
    )
      return
    setIntegrationPermissions(prev => {
      if (Object.keys(prev).length > 0) return prev
      const next = {}
      userIntegrations.forEach(int => {
        const intId = String(int._id || int.id || "")
        if (!intId) return
        if (
          int.permissions &&
          typeof int.permissions === "object" &&
          !Array.isArray(int.permissions)
        ) {
          next[intId] = { ...int.permissions }
        } else {
          const perms = {}
          intPermDefs.forEach(p => {
            perms[p.id] = false
          })
          next[intId] = perms
        }
      })
      return next
    })
  }, [userIntegrations, intPermDefs])

  useEffect(() => {
    if (mode === "edit") {
      fetchRoleDetails()
    }
  }, [mode])

  useEffect(() => {
    if (scope === "superadmin" && mode === "add") {
      setSelectedPermissions(deepCopy(filteredRolePermissionCatalog))
    }
  }, [scope, mode, filteredRolePermissionCatalog])

  useEffect(() => {
    if (
      scope !== "tenant" ||
      mode !== "add" ||
      roleBuilderLoading ||
      !tenantPermissionCatalog.length
    ) {
      return
    }
    setSelectedPermissions(deepCopy(tenantPermissionCatalog))
  }, [scope, mode, roleBuilderLoading, tenantPermissionCatalog])

  useEffect(() => {
    if (
      mode !== "edit" ||
      actionType !== "ROLE_DETAILS_SUCCESS_FETCHED" ||
      (scope === "tenant" &&
        (roleBuilderLoading || !tenantPermissionCatalog.length))
    ) {
      return
    }
    getOldValue()
  }, [
    mode,
    actionType,
    scope,
    roleBuilderLoading,
    tenantPermissionCatalog,
    roleDetails,
  ])

  const fetchRoleDetails = () => {
    const payload = {
      id,
    }
    dispatch(getRoleDetails({ ...payload, navigate }))
  }

  const getOldValue = () => {
    const { name, nameAr, description, descriptionAr, permissions, status, integrationPermissions: savedIntPerms } =
      roleDetails
    setFormValues(
      trimRoleFormValues({
        ...formValues,
        name: name || "",
        nameAr: nameAr || "",
        description: description || "",
        descriptionAr: descriptionAr || "",
      })
    )
    setRoleStatus(status)
    updatePermissionsWithStatic(permissions)
    if (savedIntPerms && typeof savedIntPerms === "object") {
      setIntegrationPermissions(savedIntPerms)
    }
  }

  // console.log(selectedPermissions)

  const updatePermissionsWithStatic = apiPermissions => {
    setSelectedPermissions(
      mergeArrays(filteredRolePermissionCatalog, apiPermissions)
    )
  }

  // Helper function to deep copy an object or array
  function deepCopy(obj) {
    return JSON.parse(JSON.stringify(obj))
  }

  // Merge plan catalog with saved role — catalog defines which rows exist; API only sets checked state.
  function mergePermissions(catalogPermissions, apiPermissions) {
    const apiList = Array.isArray(apiPermissions) ? apiPermissions : []

    return catalogPermissions.map(catalogPerm => {
      const apiPerm = apiList.find(
        p =>
          p.id === catalogPerm.id ||
          String(p.id) === String(catalogPerm.id)
      )

      const catalogSubs = catalogPerm.subPermissions || []
      const subPermissions = catalogSubs.map(sub => {
        const apiSub = apiPerm?.subPermissions?.find(
          sp =>
            sp.id === sub.id || String(sp.id) === String(sub.id)
        )
        return {
          ...deepCopy(sub),
          isEnabled: Boolean(apiSub?.isEnabled),
          enable: Boolean(apiSub?.isEnabled),
        }
      })

      const anySubEnabled = subPermissions.some(s => s.isEnabled)
      const parentEnabled = catalogSubs.length
        ? anySubEnabled
        : Boolean(apiPerm?.isEnabled)

      return {
        ...deepCopy(catalogPerm),
        isEnabled: parentEnabled,
        enable: parentEnabled,
        subPermissions,
      }
    })
  }

  // Function to merge static array with API array
  // Use staticArray (permissionsList) as source of truth so all permissions are always visible
  function mergeArrays(staticArray, apiArray) {
    const apiArraySafe = Array.isArray(apiArray) ? apiArray : []

    return staticArray.map(staticItem => {
      const existingItem = apiArraySafe.find(
        item =>
          item.id === staticItem.id ||
          String(item.id) === String(staticItem.id) ||
          Number(item.id) === Number(staticItem.id)
      )

      if (!existingItem) {
        return deepCopy(staticItem)
      }

      return {
        ...deepCopy(staticItem),
        permissions: mergePermissions(
          staticItem.permissions,
          existingItem.permissions
        ),
      }
    })
  }

  const handleSwitchClick = (categoryId, permissionId) => {
    const updatedLists = permissionLists?.map(category => {
      if (category.id === categoryId) {
        return {
          ...category,
          permissions: category?.permissions?.map(permission => {
            if (permission.id === permissionId) {
              return {
                ...permission,
                enable: !permission.enable,
              }
            }
            return permission
          }),
        }
      }
      return category
    })

    // setPermissionLists(updatedLists)
  }

  useEffect(() => {
    // CHECKED SELECT ALL SWITCH IF HAVE ALL PERMISSION CHECKED
    setSelectAll(
      selectedPermissions.every(category =>
        category.permissions.every(permission => permission.isEnabled)
      )
    )
  }, [selectedPermissions])

  const handleSelectAllToggle = () => {
    const newSelectAll = !selectAll
    setSelectAll(newSelectAll)

    const updatedPermissionLists = permissionLists?.map(category => ({
      ...category,
      permissions: category?.permissions?.map(permission => ({
        ...permission,
        enable: newSelectAll,
      })),
    }))

    // setPermissionLists(updatedPermissionLists)
  }

  const handleSubmit = values => {
    const { name, nameAr, description, descriptionAr } =
      trimRoleFormValues(values)

    // const updatedSelectedPermissions = selectedPermissions.map(selected => {
    //   const matchedPermission = permissionsList.find(
    //     permission => permission.id === selected.id
    //   )
    //   return matchedPermission
    //     ? {
    //         ...selected,
    //         titleAr: matchedPermission.titleAr,
    //         title: matchedPermission.title,
    //       }
    //     : selected
    // })

    const permissionsForSubmit = selectedPermissions.filter(category =>
      category.permissions?.some(
        permission =>
          permission.isEnabled ||
          permission.subPermissions?.some(sub => sub.isEnabled)
      )
    )

    const payload = {
      name,
      nameAr,
      description,
      descriptionAr,
      permissions: permissionsForSubmit,
      integrationPermissions,
    }

    const hasViewPermission = permissionsForSubmit.some(permission =>
      permission.permissions.some(p => p.isEnabled && p.value === "view")
    )
    if (mode === "add") {
      if (!hasViewPermission) {
        dispatch(
          showAlert({
            text: t("Please select at least one view permission."),
            type: "error",
          })
        )
      } else {
        dispatch(addNewRole({ ...payload, navigate }))
          .then(action => {
            const { statusCode, status, msg } = action?.payload
            if (statusCode === 200 && status) {
              dispatch(showAlert({ text: msg, type: "success" }))
              navigate(basePath)
            } else {
              dispatch(showAlert({ text: msg, type: "error" }))
            }
          })
          .catch(error => {})
      }
    } else if (mode === "edit") {
      const updatedPayload = {
        ...payload,
        id: id,
        status: `${roleStatus}`,
      }
      if (!hasViewPermission) {
        dispatch(
          showAlert({
            text: t("Please select at least one view permission."),
            type: "error",
          })
        )
      } else {
        dispatch(updateRoleDetails(updatedPayload))
          .then(action => {
            const { statusCode, status, msg } = action?.payload
            if (statusCode === 200 && status) {
              dispatch(showAlert({ text: msg, type: "success" }))
              dispatch(userDetails())
              navigate(-1)
            } else {
              dispatch(showAlert({ text: msg, type: "error" }))
            }
          })
          .catch(error => {})
      }
    }
  }

  const handleCheckboxChange = (
    permissionGroupId,
    permissionId,
    subPermissionId = 0
  ) => {
    // console.log(permissionGroupId, permissionId, subPermissionId)

    const updatedLists = selectedPermissions.map(category => {
      if (category.id === permissionGroupId) {
        return {
          ...category,
          permissions: category.permissions.map(permission => {
            if (permission.id === permissionId) {
              if (subPermissionId === 0) {
                const newStatus = !permission.isEnabled
                return {
                  ...permission,
                  isEnabled: newStatus,
                  enable: newStatus,
                  subPermissions: permission.subPermissions?.map(
                    subPermission => ({
                      ...subPermission,
                      isEnabled: newStatus,
                      enable: newStatus,
                    })
                  ),
                }
              } else {
                const subPermissions = (permission.subPermissions || []).map(
                  subPermission => {
                    if (subPermission.id === subPermissionId) {
                      const subStatus = !subPermission.isEnabled
                      return {
                        ...subPermission,
                        isEnabled: subStatus,
                        enable: subStatus,
                      }
                    }
                    return subPermission
                  }
                )
                return {
                  ...permission,
                  isEnabled: permission.isEnabled,
                  enable: permission.enable,
                  subPermissions,
                }
              }
            }
            return permission
          }),
        }
      }
      return category
    })

    setSelectedPermissions(updatedLists)
  }

  const handleSelectAllPermissions = () => {
    const newSelectAll = !selectAll
    const updatedPermissionLists = selectedPermissions?.map(category => ({
      ...category,
      permissions: category?.permissions?.map(permission => ({
        ...permission,
        isEnabled: newSelectAll,
        enable: newSelectAll,
        subPermissions: permission.subPermissions?.map(sub => ({
          ...sub,
          isEnabled: newSelectAll,
          enable: newSelectAll,
        })),
      })),
    }))
    setSelectedPermissions(updatedPermissionLists)
    setIntegrationPermissions(prev => {
      const next = {}
      Object.keys(prev).forEach(intId => {
        const perms = {}
        intPermDefs.forEach(p => {
          perms[p.id] = newSelectAll
        })
        next[intId] = perms
      })
      return next
    })
  }

  const handleIntegrationPermChange = (integrationId, permId) => {
    setIntegrationPermissions(prev => ({
      ...prev,
      [integrationId]: {
        ...prev[integrationId],
        [permId]: !prev[integrationId]?.[permId],
      },
    }))
  }

  const handleIntegrationSelectAll = (integrationId, setAll) => {
    setIntegrationPermissions(prev => {
      const perms = {}
      intPermDefs.forEach(p => {
        perms[p.id] = setAll
      })
      return { ...prev, [integrationId]: perms }
    })
  }

  const handleCategorySelectAll = (permissionGroupId, selectAll) => {
    setSelectedPermissions(prev =>
      prev.map(category => {
        if (
          category.id !== permissionGroupId &&
          String(category.id) !== String(permissionGroupId)
        ) {
          return category
        }
        return {
          ...category,
          permissions: category.permissions.map(permission => ({
            ...permission,
            isEnabled: selectAll,
            subPermissions: permission.subPermissions?.map(sub => ({
              ...sub,
              isEnabled: selectAll,
            })),
          })),
        }
      })
    )
  }

  const handleToggleIntegrationOpen = intKey => {
    setOpenIntegrationIds(prev =>
      prev.includes(intKey) ? prev.filter(k => k !== intKey) : [...prev, intKey]
    )
  }

  if (
    (mode === "edit" && loading) ||
    (scope === "tenant" && roleBuilderLoading)
  ) {
    return <Spinners />
  }

  return (
    <div className="page-content add_roles_page">
      <Breadcrumbs />
      <Row>
        <Col xl={12}>
          <Formik
            enableReinitialize={true}
            initialValues={formValues}
            validationSchema={addRoleSchemas}
            validateOnChange
            validateOnBlur
            onSubmit={handleSubmit}
          >
            {({ errors, touched, setFieldValue, handleBlur }) => {
              const trimBlur = createTrimOnBlur(setFieldValue, handleBlur)
              return (
              <Form action="">
                <Card className="role-permissions-sticky-card">
                  <CardBody className="role-permissions-card-body">
                    <CardTitle className="d-flex align-items-center">
                      <BackButton />
                      {mode === "add" ? t("Add Role") : t("Edit Role")}
                    </CardTitle>
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="name">
                            <span className="text-danger">*</span>{" "}
                            {t("Name(English)")}
                          </label>
                          <Field
                            type="text"
                            id="name"
                            name="name"
                            placeholder={t("Name(English)")}
                            onBlur={trimBlur("name")}
                            className={`form-control ${
                              touched.name && errors.name ? "is-invalid" : ""
                            }`}
                          />
                          <TextError name="name" />
                        </div>
                      </div>

                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="nameAr">
                            <span className="text-danger">*</span>{" "}
                            {t("Name(عربي)")}
                          </label>
                          <Field
                            type="text"
                            style={{ direction: "rtl" }}
                            id="nameAr"
                            name="nameAr"
                            placeholder={t("Name(عربي)")}
                            onBlur={trimBlur("nameAr")}
                            className={`form-control ${
                              touched.nameAr && errors.nameAr
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <TextError name="nameAr" />
                        </div>
                      </div>

                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="description">
                            {t("Description(English)")}
                          </label>
                          <Field
                            as="textarea"
                            id="description"
                            name="description"
                            rows={3}
                            placeholder={t("Description(English)")}
                            onBlur={trimBlur("description")}
                            className={`form-control ${
                              touched.description && errors.description
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <TextError name="description" />
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="form_field">
                          <label htmlFor="descriptionAr">
                            {t("Description(عربي)")}
                          </label>
                          <Field
                            as="textarea"
                            rows={3}
                            style={{ direction: "rtl" }}
                            id="descriptionAr"
                            name="descriptionAr"
                            placeholder={t("Description(عربي)")}
                            onBlur={trimBlur("descriptionAr")}
                            className={`form-control ${
                              touched.descriptionAr && errors.descriptionAr
                                ? "is-invalid"
                                : ""
                            }`}
                          />
                          <TextError name="descriptionAr" />
                        </div>
                      </div>
                      {/* <h3 className="my-3">Permissions</h3> */}
                      {/* {!permissionLists || permissionLists.length === 0 ? (
                        <p>No permissions found</p>
                      ) : (
                        <div className="">
                          <div className="d-flex align-items-center mb-3">
                            <h6 className="me-2">Select All Permission</h6>
                            <Switch
                              isChecked={selectAll}
                              onClickHandler={handleSelectAllToggle}
                            />
                          </div>
                          <Table striped bordered hover>
                            <thead>
                              <tr>
                                <th>S. No.</th>
                                <th>Category</th>
                                <th>Lists</th>
                                <th>Create</th>
                                <th>Edit</th>
                                <th>Delete</th>
                              </tr>
                            </thead>
                            <tbody>
                              {permissionLists.map((category, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{category.category}</td>
                                  {category.permissions.map(
                                    ({ id, name, enable }) => (
                                      <td key={id}>
                                        <div className="single_switch">
                                          <Switch
                                            isChecked={enable}
                                            onClickHandler={() =>
                                              handleSwitchClick(category.id, id)
                                            }
                                          />
                                        </div>
                                      </td>
                                    )
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )} */}
                    </div>
                  </CardBody>
                </Card>
                <Card>
                  <CardBody>
                    <CardTitle className="d-flex align-items-center">
                      {t("Permissions")}{" "}
                      <div className="form-check ps-5">
                        <input
                          className="form-check-input"
                          id={"selectAll"}
                          type="checkbox"
                          name={"selectAll"}
                          onClick={handleSelectAllPermissions}
                          checked={selectAll}
                          readOnly
                        />
                        <label
                          className="form-check-label"
                          htmlFor={"selectAll"}
                        >
                          {selectAll ? t("Deselect") : t("Select")}{" "}
                          {t("All Permissions")}
                        </label>
                      </div>
                    </CardTitle>
                    <div className="mt-3">
                      <RolePermissionsSelector
                      t={t}
                      language={language}
                      permissionGroups={displayPermissionGroups}
                      showIntegrations={showIntegrationsPanel}
                      integrationsTitle={t("Integrations")}
                      catalogTree={integrationsCatalogTree}
                      catalogTreeLoading={roleBuilderLoading && showIntegrationsPanel}
                      fallbackIntegrations={userIntegrations}
                      integrationPermissions={integrationPermissions}
                      intPermDefs={intPermDefs}
                      openIntegrationIds={openIntegrationIds}
                      onToggleIntegrationOpen={handleToggleIntegrationOpen}
                      onPermissionChange={handleCheckboxChange}
                      onCategorySelectAll={handleCategorySelectAll}
                      onIntegrationPermChange={handleIntegrationPermChange}
                      onIntegrationSelectAll={handleIntegrationSelectAll}
                    />
                    </div>
                    <div className="col-12 mt-4">
                      <div className="d-flex align-items-center justify-content-end">
                        <Button
                          type="button"
                          color="secondary"
                          className=" mx-2"
                          onClick={() => navigate(-1)}
                        >
                          {t("Cancel")}
                        </Button>
                        <Button
                          color="success"
                          type="submit"
                          style={{ width: "fit-content" }}
                          disabled={isLoading}
                        >
                          {mode === "add" ? t("Add") : t("Save")}
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Form>
              )
            }}
          </Formik>
        </Col>
      </Row>
    </div>
  )
}

export default AddRoles

// permissionsList
// setSelectedPermissions
